import React from 'react';
import {
  Badge,
  Button,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'i18n/TranslationProvider';
import { PageShell, PageHeader } from 'components/layout/PageShell';
import {
  UnifiedDataTableShell,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTabsStyles, useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';

// Placeholder rows — replaced when the payments API is available.
const CLIENT_PAYMENTS = [];
const PARTNER_PAYOUTS = [];
const PLATFORM_FEES = [];

function ClientPaymentsTab() {
  const { t } = useTranslation();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const { tableSx } = useUnifiedTableStyles();

  return (
    <UnifiedDataTableShell
      isEmpty={CLIENT_PAYMENTS.length === 0}
      emptyTitle={t('No client payments recorded yet')}
      emptyDescription={t('Client payment rows will appear here when the payments backend is connected.')}
    >
      <Table size="sm" variant="simple" sx={tableSx}>
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>{t('Client')}</Th>
            <Th>{t('Listing')}</Th>
            <Th>{t('Amount')}</Th>
            <Th>{t('Method')}</Th>
            <Th>{t('Status')}</Th>
            <Th>{t('Date')}</Th>
            <Th>{t('Actions')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {CLIENT_PAYMENTS.map((payment, index) => (
            <Tr key={payment.id}>
              <Td color={mutedText} fontSize="xs">{index + 1}</Td>
              <Td>{payment.client}</Td>
              <Td>{payment.listing}</Td>
              <Td>{payment.amount}</Td>
              <Td>{payment.method}</Td>
              <Td>
                <Badge
                  colorScheme={payment.status === 'paid' ? 'green' : 'yellow'}
                  borderRadius="full"
                  px={2}
                >
                  {payment.status}
                </Badge>
              </Td>
              <Td color={mutedText} fontSize="xs">{payment.date}</Td>
              <Td>
                <Button size="xs" variant="outline" colorScheme="green">
                  {t('Mark Paid')}
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </UnifiedDataTableShell>
  );
}

function PartnerPayoutsTab() {
  const { t } = useTranslation();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const { tableSx } = useUnifiedTableStyles();

  return (
    <UnifiedDataTableShell
      isEmpty={PARTNER_PAYOUTS.length === 0}
      emptyTitle={t('No partner payouts recorded yet')}
      emptyDescription={t('Payout rows will appear here once partner payout processing is enabled.')}
    >
      <Table size="sm" variant="simple" sx={tableSx}>
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>{t('Partner')}</Th>
            <Th>{t('Listing')}</Th>
            <Th>{t('Payout Amount')}</Th>
            <Th>{t('Status')}</Th>
            <Th>{t('Date')}</Th>
            <Th>{t('Actions')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {PARTNER_PAYOUTS.map((payout, index) => (
            <Tr key={payout.id}>
              <Td color={mutedText} fontSize="xs">{index + 1}</Td>
              <Td>{payout.partner}</Td>
              <Td>{payout.listing}</Td>
              <Td>{payout.amount}</Td>
              <Td>
                <Badge
                  colorScheme={payout.status === 'paid' ? 'green' : 'yellow'}
                  borderRadius="full"
                  px={2}
                >
                  {payout.status}
                </Badge>
              </Td>
              <Td color={mutedText} fontSize="xs">{payout.date}</Td>
              <Td>
                <Button size="xs" variant="outline" colorScheme="green">
                  {t('Mark Paid')}
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </UnifiedDataTableShell>
  );
}

function PlatformFeesTab() {
  const { t } = useTranslation();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const { tableSx } = useUnifiedTableStyles();

  return (
    <UnifiedDataTableShell
      isEmpty={PLATFORM_FEES.length === 0}
      emptyTitle={t('No platform fees recorded yet')}
      emptyDescription={t('Fee rows will appear here once payment settlement data is available.')}
    >
      <Table size="sm" variant="simple" sx={tableSx}>
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>{t('Listing')}</Th>
            <Th>{t('Gross')}</Th>
            <Th>{t('Fee (20%)')}</Th>
            <Th>{t('Date')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {PLATFORM_FEES.map((fee, index) => (
            <Tr key={fee.id}>
              <Td color={mutedText} fontSize="xs">{index + 1}</Td>
              <Td>{fee.listing}</Td>
              <Td>{fee.gross}</Td>
              <Td fontWeight="600">{fee.fee}</Td>
              <Td color={mutedText} fontSize="xs">{fee.date}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </UnifiedDataTableShell>
  );
}

export default function AdminPayments() {
  const { t } = useTranslation();
  const { tabListProps, tabProps } = useUnifiedTabsStyles();

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={t('Payments')}
          description={t('Client payments, partner payouts, and platform fee tracking.')}
        />

        <UnifiedSurfaceCard p={5}>
          <Tabs variant="unstyled">
            <TabList mb={4} flexWrap="wrap" {...tabListProps}>
              <Tab {...tabProps}>{t('Client Payments')}</Tab>
              <Tab {...tabProps}>{t('Partner Payouts')}</Tab>
              <Tab {...tabProps}>{t('Platform Fees')}</Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0}>
                <ClientPaymentsTab />
              </TabPanel>
              <TabPanel px={0}>
                <PartnerPayoutsTab />
              </TabPanel>
              <TabPanel px={0}>
                <PlatformFeesTab />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </UnifiedSurfaceCard>
      </VStack>
    </PageShell>
  );
}
